import React, { useState, useEffect, useMemo } from 'react';
import { Card, Typography, Table, Select, DatePicker, Button, Tag, Space, Spin, Empty, message } from 'antd';
import { DownloadOutlined, FilePdfOutlined, ClusterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { fetchDashboardFilterOptions } from '../../../../Service/Dashboard_API/dashboardApi';
import { exportClusterAttendanceCSV } from '../../../../Service/Clusters_API/attendanceApi';

const { Text } = Typography;
const { Option } = Select;

// Helper to determine active / current academic year
const getActiveAcademicYear = (years) => {
  if (!Array.isArray(years) || years.length === 0) return null;
  
  // 1. Check status is active
  const activeYear = years.find(
    y => y.status?.toLowerCase() === 'active' || y.status === 'Active' || y.status === 'ACTIVE' || y.status === 1 || y.status === '1'
  );
  if (activeYear) return activeYear;

  // 2. Check current date falls within start_date and end_date
  const today = dayjs();
  const dateMatched = years.find(y => {
    if (!y.start_date || !y.end_date) return false;
    const start = dayjs(y.start_date);
    const end = dayjs(y.end_date);
    return !today.isBefore(start, 'day') && !today.isAfter(end, 'day');
  });
  if (dateMatched) return dateMatched;

  // 3. Fallback to first academic year
  return years[0];
};

const getInitialMonthForAy = (ay) => {
  const today = dayjs();
  if (!ay) return today;
  const startDate = ay.start_date ? dayjs(ay.start_date) : null;
  const endDate = ay.end_date ? dayjs(ay.end_date) : null;
  if (endDate && today.isAfter(endDate, 'day')) {
    return endDate;
  }
  if (startDate && today.isBefore(startDate, 'day')) {
    return startDate;
  }
  return today;
};

export const ClusterLiveAttendanceReportCard = ({
  academicYears = [],
  clusters = [],
  defaultAcademicYearId = null,
  userRoleOverride = null,
  userAssignedClustersOverride = null
}) => {
  const user = useSelector((state) => state.auth?.user);
  const role = userRoleOverride || user?.role || localStorage.getItem('role');

  const [internalAcademicYears, setInternalAcademicYears] = useState([]);

  // Fetch academic years internally as fallback if not provided via props
  useEffect(() => {
    if (!academicYears || academicYears.length === 0) {
      fetchDashboardFilterOptions()
        .then(res => {
          if (res?.success && res.academicYears?.length > 0) {
            setInternalAcademicYears(res.academicYears);
          }
        })
        .catch(err => console.error('Failed to fetch filter options in ClusterLiveAttendanceReportCard:', err));
    }
  }, [academicYears]);

  const effectiveAcademicYears = useMemo(() => {
    return (academicYears && academicYears.length > 0) ? academicYears : internalAcademicYears;
  }, [academicYears, internalAcademicYears]);

  // Determine initial active academic year object
  const initialActiveAy = useMemo(() => {
    if (defaultAcademicYearId && effectiveAcademicYears.length > 0) {
      const found = effectiveAcademicYears.find(y => String(y.academic_year_id) === String(defaultAcademicYearId));
      if (found) return found;
    }
    return getActiveAcademicYear(effectiveAcademicYears);
  }, [effectiveAcademicYears, defaultAcademicYearId]);

  const [selectedAcademicYear, setSelectedAcademicYear] = useState(() => {
    return initialActiveAy ? String(initialActiveAy.academic_year_id) : '';
  });
  const [selectedMonth, setSelectedMonth] = useState(() => {
    return getInitialMonthForAy(initialActiveAy);
  });
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const [selectedCluster, setSelectedCluster] = useState('all');
  
  const [loading, setLoading] = useState(false);
  const [rawExportData, setRawExportData] = useState({ dates: [], data: [] });

  // Filter clusters based on Academic Year and Studio role access
  const allowedClusters = useMemo(() => {
    if (!clusters || clusters.length === 0) return [];
    
    let filtered = clusters;

    // Filter clusters strictly by selected academic year
    if (selectedAcademicYear && selectedAcademicYear !== 'all') {
      const yearFiltered = clusters.filter(c => 
        c.academic_year_id && String(c.academic_year_id) === String(selectedAcademicYear)
      );
      if (yearFiltered.length > 0) {
        filtered = yearFiltered;
      }
    }
    
    // If role is STUDIO or instructor/non-admin, filter by assigned cluster
    if (role === 'STUDIO' || (role !== 'ADMIN' && role !== 'SUPER_ADMIN')) {
      const userClusterId = userAssignedClustersOverride || user?.cluster_id || user?.assigned_clusters;
      if (userClusterId) {
        const ids = Array.isArray(userClusterId) 
          ? userClusterId.map(id => String(id))
          : [String(userClusterId)];
        
        filtered = filtered.filter(c => ids.includes(String(c.cluster_id)) || ids.includes(String(c.cluster_name)));
      }
    }

    // Deduplicate by cluster_id so each cluster appears once in dropdown
    const uniqueMap = new Map();
    filtered.forEach(c => {
      const cId = String(c.cluster_id || c.cluster_name);
      if (cId && !uniqueMap.has(cId)) {
        uniqueMap.set(cId, c);
      }
    });

    return Array.from(uniqueMap.values());
  }, [clusters, role, user, userAssignedClustersOverride, selectedAcademicYear]);

  // Set default academic year to current active academic year when academic years load
  useEffect(() => {
    if (effectiveAcademicYears && effectiveAcademicYears.length > 0) {
      const isValid = selectedAcademicYear && effectiveAcademicYears.some(y => String(y.academic_year_id) === String(selectedAcademicYear));
      if (!isValid) {
        const activeAy = (defaultAcademicYearId && effectiveAcademicYears.find(y => String(y.academic_year_id) === String(defaultAcademicYearId)))
          || getActiveAcademicYear(effectiveAcademicYears);
        if (activeAy) {
          const ayId = String(activeAy.academic_year_id);
          setSelectedAcademicYear(ayId);
          setSelectedMonth(getInitialMonthForAy(activeAy));
          setSelectedDateRange(null);
        }
      }
    }
  }, [effectiveAcademicYears, defaultAcademicYearId, selectedAcademicYear]);

  // Sync selectedMonth when selectedAcademicYear changes
  useEffect(() => {
    if (effectiveAcademicYears && effectiveAcademicYears.length > 0 && selectedAcademicYear && selectedAcademicYear !== 'all') {
      const currAy = effectiveAcademicYears.find(ay => String(ay.academic_year_id) === String(selectedAcademicYear));
      if (currAy) {
        setSelectedMonth(getInitialMonthForAy(currAy));
        setSelectedDateRange(null);
      }
    }
  }, [selectedAcademicYear, effectiveAcademicYears]);

  // Set default selectedCluster for Studio or Admin
  useEffect(() => {
    if (allowedClusters.length > 0) {
      const exists = allowedClusters.some(c => String(c.cluster_id) === String(selectedCluster));
      if (!exists) {
        if (role === 'STUDIO' || (role !== 'ADMIN' && role !== 'SUPER_ADMIN')) {
          setSelectedCluster(String(allowedClusters[0].cluster_id));
        } else {
          setSelectedCluster('all');
        }
      }
    }
  }, [allowedClusters, role, selectedCluster]);

  // Fetch report data whenever filters change - only fetch when academic year is set
  useEffect(() => {
    if (!selectedAcademicYear || selectedAcademicYear === 'all') {
      return;
    }

    const loadReportData = async () => {
      let clusterToFetch = selectedCluster;
      if (role === 'STUDIO' || (role !== 'ADMIN' && role !== 'SUPER_ADMIN')) {
        if (allowedClusters.length > 0) {
          clusterToFetch = String(allowedClusters[0].cluster_id);
        } else {
          setRawExportData({ dates: [], data: [] });
          return;
        }
      }
      if (!clusterToFetch) return;
      setLoading(true);
      try {
        const monthStr = selectedMonth ? selectedMonth.format('YYYY-MM') : null;
        const startDateStr = (selectedDateRange && selectedDateRange[0]) ? selectedDateRange[0].format('YYYY-MM-DD') : null;
        const endDateStr = (selectedDateRange && selectedDateRange[1]) ? selectedDateRange[1].format('YYYY-MM-DD') : null;
        const res = await exportClusterAttendanceCSV(
          clusterToFetch,
          null,
          monthStr,
          selectedAcademicYear,
          startDateStr,
          endDateStr
        );
        setRawExportData({
          dates: res.dates || [],
          data: res.data || []
        });
      } catch (err) {
        console.error('Failed to load cluster live attendance report:', err);
        setRawExportData({ dates: [], data: [] });
      } finally {
        setLoading(false);
      }
    };

    loadReportData();
  }, [selectedCluster, selectedMonth, selectedDateRange, selectedAcademicYear, allowedClusters, role]);

  // Process data per cluster & standard according to exact calculation rules:
  // 1. TOTAL CLASS DONE = Count of valid attendance dates where is_class_canceled == 0
  // 2. Canceled classes & missing attendance dates DO NOT count as classes done.
  // 3. ATTENDED = Count of valid dates where school status is PRESENT / numeric > 0 / VOD.
  // 4. MONTHLY % = (ATTENDED / TOTAL CLASS DONE) * 100%
  const reportData = useMemo(() => {
    const { dates = [], data = [] } = rawExportData;
    if (!data || data.length === 0) return [];

    // Filter strictly for allowed clusters if STUDIO / non-admin role
    let filteredData = data;
    if (role === 'STUDIO' || (role !== 'ADMIN' && role !== 'SUPER_ADMIN')) {
      const allowedNames = allowedClusters.map(c => String(c.cluster_name || '').trim().toLowerCase());
      const allowedIds = allowedClusters.map(c => String(c.cluster_id));
      filteredData = data.filter(row => 
        allowedNames.includes(String(row.cluster_name || '').trim().toLowerCase()) || 
        allowedIds.includes(String(row.cluster_id || ''))
      );
    }
    if (filteredData.length === 0) return [];

    // Group rows by cluster_name
    const clusterGroups = {};
    filteredData.forEach(row => {
      const cName = row.cluster_name || 'Cluster';
      if (!clusterGroups[cName]) clusterGroups[cName] = [];
      clusterGroups[cName].push(row);
    });

    const resultClusters = [];

    Object.keys(clusterGroups).forEach(cName => {
      const rows = clusterGroups[cName];
      
      // Group by standard_name
      const stdMap = {};
      rows.forEach(r => {
        const sName = r.standard_name || 'Standard';
        if (!stdMap[sName]) stdMap[sName] = [];
        stdMap[sName].push(r);
      });

      const standardsList = Object.keys(stdMap).map(sName => {
        const stdRows = stdMap[sName];
        
        // A date d is a valid conducted class for standard sName ONLY IF:
        // 1. Attendance was actually recorded for standard sName on date d (at least 1 school has an attendance record)
        // 2. AND the class was NOT canceled for the cluster (is_class_canceled == 0)
        const validDates = dates.filter(d => {
          let hasAttendanceRecord = false;
          let isCanceled = false;

          stdRows.forEach(r => {
            const val = r[d];
            if (typeof val === 'string' && val.startsWith('CANCELED:')) {
              isCanceled = true;
            } else if (val !== undefined && val !== null && val !== '' && val !== '-') {
              hasAttendanceRecord = true;
            }
          });

          return hasAttendanceRecord && !isCanceled;
        });

        const totalClassDone = validDates.length;

        return {
          standard_name: sName,
          total_class_done: totalClassDone,
          valid_dates: validDates
        };
      })
      .filter(s => s.total_class_done > 0)
      .sort((a, b) => {
        const getNum = (str) => {
          const match = String(str || '').match(/(\d+)/);
          return match ? parseInt(match[1], 10) : null;
        };
        const numA = getNum(a.standard_name);
        const numB = getNum(b.standard_name);
        if (numA !== null && numB !== null && numA !== numB) {
          return numA - numB;
        }
        return String(a.standard_name || '').localeCompare(String(b.standard_name || ''), undefined, { numeric: true, sensitivity: 'base' });
      });

      // If no classes were conducted for any standard in this cluster, do not show this cluster
      if (standardsList.length === 0) {
        return;
      }

      // Build unique schools list for this cluster
      const schoolMap = new Map();
      rows.forEach(r => {
        const sName = r.standard_name || 'Standard';
        const stdInfo = standardsList.find(s => s.standard_name === sName);
        if (!stdInfo) return;

        const code = r.code || r.institution_code || '-';
        const name = r.name || r.institution_name || '-';
        const key = `${code}-${name}`;
        if (!schoolMap.has(key)) {
          schoolMap.set(key, { code, name, standardsData: {} });
        }
        
        const schoolObj = schoolMap.get(key);
        const validDates = stdInfo ? stdInfo.valid_dates : [];
        const totalClassDone = stdInfo ? stdInfo.total_class_done : 0;

        // Count attended days among validDates
        let attended = 0;
        validDates.forEach(d => {
          const val = r[d];
          if (val === 'P' || (!isNaN(val) && val !== '' && Number(val) > 0) || val === 'VOD') {
            attended += 1;
          }
        });

        const pct = totalClassDone > 0 ? ((attended / totalClassDone) * 100).toFixed(2) : '0.00';

        schoolObj.standardsData[sName] = {
          attended,
          total_class_done: totalClassDone,
          percentage: `${pct}%`
        };
      });

      resultClusters.push({
        cluster_name: cName,
        standards: standardsList,
        schools: Array.from(schoolMap.values())
      });
    });

    return resultClusters;
  }, [rawExportData, allowedClusters, role]);

  // Export CSV matching exact report format
  const handleExportCSV = () => {
    if (reportData.length === 0) {
      return message.warning('No report data available to export.');
    }

    let csvContent = '\uFEFF'; // UTF-8 BOM
    const displayTitle = monthLabel;
    const headerPct = pctTitle;

    reportData.forEach(cGroup => {
      // Title
      csvContent += `${cGroup.cluster_name} - ATTENDANCE SUMMARY (${displayTitle})\n`;

      // Header row
      const headers = ['S-CODE', 'SCHOOLNAME'];
      cGroup.standards.forEach(std => {
        headers.push(`${std.standard_name} ATTENDED`, headerPct);
      });
      csvContent += headers.join(',') + '\n';

      // School rows
      cGroup.schools.forEach(school => {
        const row = [school.code, `"${school.name}"`];
        cGroup.standards.forEach(std => {
          const sData = school.standardsData[std.standard_name] || { attended: 0, percentage: '0.00%' };
          row.push(sData.attended, sData.percentage);
        });
        csvContent += row.join(',') + '\n';
      });

      // Footer Row (Total Class Done)
      const footerRow = [`"${displayTitle}"`, ''];
      cGroup.standards.forEach(std => {
        footerRow.push('TOTAL CLASS DONE', std.total_class_done);
      });
      csvContent += footerRow.join(',') + '\n\n';
    });

    let fileSuffix = 'Full_Year';
    if (selectedDateRange && selectedDateRange[0] && selectedDateRange[1]) {
      fileSuffix = `${selectedDateRange[0].format('YYYY_MM_DD')}_to_${selectedDateRange[1].format('YYYY_MM_DD')}`;
    } else if (selectedMonth) {
      fileSuffix = selectedMonth.format('YYYY_MM');
    }
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Cluster_Attendance_Report_${fileSuffix}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export PDF / Print matching exact screenshot table style - 1 table per page fit
  const handleExportPDF = () => {
    if (reportData.length === 0) {
      return message.warning('No report data available to print/export PDF.');
    }

    const displayTitle = monthLabel;
    const headerPct = pctTitle;

    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Cluster Attendance Live Summary Report - ${displayTitle}</title>
        <style>
          @page { size: A4 landscape; margin: 8mm; }
          * { box-sizing: border-box; }
          body { font-family: Arial, sans-serif; color: #111; margin: 0; padding: 0; background: #fff; }
          .pdf-page {
            page-break-after: always;
            break-after: page;
            page-break-inside: avoid;
            break-inside: avoid;
            padding: 4px;
            width: 100%;
          }
          .pdf-page:last-child {
            page-break-after: avoid;
            break-after: avoid;
          }
          .header-title { text-align: center; text-transform: uppercase; margin: 0 0 2px 0; font-size: 15px; font-weight: bold; color: #002140; }
          .header-sub { text-align: center; font-size: 11px; font-weight: bold; color: #1890ff; margin-bottom: 8px; text-transform: uppercase; }
          .cluster-banner { background: #f0f5ff; border: 1px solid #adc6ff; padding: 6px 12px; font-size: 13px; font-weight: bold; color: #002140; margin-bottom: 8px; text-transform: uppercase; border-radius: 4px; display: flex; justify-content: space-between; align-items: center; }
          table { width: 100%; border-collapse: collapse; page-break-inside: avoid; table-layout: auto; }
          th, td { border: 1px solid #333; text-align: center; word-wrap: break-word; }
          th { background-color: #f2f2f2; font-weight: bold; }
          td.left { text-align: left; }
          .footer-row td { font-weight: bold; background-color: #fafafa; border-top: 2px solid #000; }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
    `;

    reportData.forEach((cGroup) => {
      const schoolCount = cGroup.schools.length;
      let fontSize = '11px';
      let padding = '5px 7px';

      if (schoolCount > 25) {
        fontSize = '8px';
        padding = '2px 4px';
      } else if (schoolCount > 18) {
        fontSize = '9px';
        padding = '3px 5px';
      } else if (schoolCount > 12) {
        fontSize = '10px';
        padding = '4px 6px';
      }

      html += `
        <div class="pdf-page">
          <div class="header-title">VEDIKA PORTAL - CLUSTER ATTENDANCE SUMMARY</div>
          <div class="header-sub">${displayTitle}</div>
          <div class="cluster-banner">
            <span>📍 CLUSTER: ${cGroup.cluster_name}</span>
            <span style="font-size: 11px; font-weight: normal; color: #595959;">Generated on ${dayjs().format('DD-MM-YYYY HH:mm')}</span>
          </div>
          <table style="font-size: ${fontSize};">
            <thead>
              <tr>
                <th style="width: 80px; padding: ${padding};">S-CODE</th>
                <th style="text-align: left; padding: ${padding};">SCHOOLNAME</th>
      `;

      cGroup.standards.forEach(std => {
        html += `
          <th style="padding: ${padding};">${std.standard_name.toUpperCase()} ATTENDED</th>
          <th style="padding: ${padding};">${headerPct}</th>
        `;
      });

      html += `
              </tr>
            </thead>
            <tbody>
      `;

      cGroup.schools.forEach(school => {
        html += `
          <tr>
            <td style="padding: ${padding};"><strong>${school.code}</strong></td>
            <td class="left" style="padding: ${padding};">${school.name}</td>
        `;

        cGroup.standards.forEach(std => {
          const sData = school.standardsData[std.standard_name] || { attended: 0, percentage: '0.00%' };
          html += `
            <td style="padding: ${padding};">${sData.attended}</td>
            <td style="padding: ${padding};"><strong>${sData.percentage}</strong></td>
          `;
        });

        html += `</tr>`;
      });

      // Footer Row
      html += `
        <tr class="footer-row">
          <td colspan="2" class="left" style="padding: ${padding}; font-size: ${fontSize};"><strong>${displayTitle}</strong></td>
      `;

      cGroup.standards.forEach(std => {
        html += `
          <td style="padding: ${padding}; font-size: ${fontSize};">TOTAL CLASS DONE</td>
          <td style="padding: ${padding}; font-size: ${fontSize};"><strong>${std.total_class_done}</strong></td>
        `;
      });

      html += `
        </tr>
        </tbody>
        </table>
        </div>
      `;
    });

    html += `
        <script>
          window.onload = function() { window.print(); };
        </script>
      </body>
      </html>
    `;

    const printWin = window.open('', '_blank');
    if (printWin) {
      printWin.document.write(html);
      printWin.document.close();
    } else {
      message.error('Pop-up blocked! Please allow pop-ups to print PDF.');
    }
  };

  const selectedAyObj = (effectiveAcademicYears || []).find(ay => String(ay.academic_year_id) === String(selectedAcademicYear));
  let monthLabel = '';
  if (selectedDateRange && selectedDateRange[0] && selectedDateRange[1]) {
    monthLabel = `${selectedDateRange[0].format('DD-MM-YYYY')} TO ${selectedDateRange[1].format('DD-MM-YYYY')}`;
  } else if (selectedMonth) {
    monthLabel = selectedMonth.format('MMMM YYYY').toUpperCase();
  } else if (selectedAyObj) {
    monthLabel = `${selectedAyObj.academic_year} FULL ACADEMIC YEAR`;
  } else {
    monthLabel = 'FULL ACADEMIC YEAR';
  }
  const pctTitle = selectedDateRange 
    ? 'PERIOD %' 
    : (selectedMonth ? 'MONTHLY %' : 'OVERALL %');

  return (
    <Card
      title={
        <Space>
          <ClusterOutlined style={{ color: '#1890ff', fontSize: 20 }} />
          <Text strong style={{ fontSize: 16, color: 'var(--text-color, #1f2937)' }}>
            Cluster Attendance Summary
          </Text>
        </Space>
      }
      extra={
        <Space size="small" wrap style={{ display: 'flex', alignItems: 'center' }}>
          <Select showSearch optionFilterProp="children"
            size="small"
            value={selectedAcademicYear || undefined}
            onChange={setSelectedAcademicYear}
            placeholder="Academic Year"
            style={{ width: 140 }}
          >
            {(effectiveAcademicYears || []).map(ay => (
              <Option key={ay.academic_year_id} value={String(ay.academic_year_id)}>
                {ay.academic_year}
              </Option>
            ))}
          </Select>

          <Select showSearch optionFilterProp="children"
            size="small"
            value={selectedCluster}
            onChange={setSelectedCluster}
            style={{ width: 180 }}
          >
            {role === 'ADMIN' || role === 'SUPER_ADMIN' ? (
              <Option value="all">All Clusters</Option>
            ) : null}
            {allowedClusters.map(c => (
              <Option key={c.cluster_id} value={String(c.cluster_id)}>
                {c.cluster_name}
              </Option>
            ))}
          </Select>

          <DatePicker 
            picker="month"
            size="small"
            value={selectedMonth}
            onChange={(val) => {
              setSelectedMonth(val);
              if (val) {
                setSelectedDateRange(null);
              }
            }}
            allowClear={true}
            placeholder="All Months (Full Year)"
            style={{ width: 160 }}
          />

          <DatePicker.RangePicker 
            size="small"
            value={selectedDateRange}
            onChange={(dates) => {
              setSelectedDateRange(dates);
              if (dates) {
                setSelectedMonth(null);
              }
            }}
            allowClear={true}
            placeholder={['Start Date', 'End Date']}
            style={{ width: 220 }}
          />

          <Button 
            size="small" 
            type="dashed" 
            icon={<DownloadOutlined />} 
            onClick={handleExportCSV}
            style={{ marginLeft: 8 }}
          >
            CSV
          </Button>
          <Button 
            size="small" 
            type="primary" 
            danger 
            icon={<FilePdfOutlined />} 
            onClick={handleExportPDF}
          >
            PDF
          </Button>
        </Space>
      }
      style={{
        borderRadius: 16,
        marginBottom: 24,
        background: 'var(--panel-color, #ffffff)',
        borderColor: 'var(--border-color, rgba(0,0,0,0.08))'
      }}
      bodyStyle={{ padding: 16 }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" tip="Loading Cluster Live Attendance Report..." />
        </div>
      ) : reportData.length === 0 ? (
        <Empty description="No classes conducted for the selected cluster and date range." image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        reportData.map((cGroup, gIdx) => {
          // Construct Antd columns dynamically matching the exact screenshot layout:
          // S-CODE | SCHOOLNAME | [STD 1] ATTENDED | MONTHLY % / OVERALL % | [STD 2] ATTENDED ...
          const columns = [
            {
              title: 'S-CODE',
              dataIndex: 'code',
              key: 'code',
              width: 90,
              render: (text) => <span style={{ fontWeight: 'bold' }}>{text}</span>
            },
            {
              title: 'SCHOOLNAME',
              dataIndex: 'name',
              key: 'name',
              render: (text) => <span style={{ fontWeight: 600 }}>{text}</span>
            }
          ];

          cGroup.standards.forEach(std => {
            columns.push({
              title: `${std.standard_name.toUpperCase()} ATTENDED`,
              key: `${std.standard_name}_attended`,
              align: 'center',
              width: 170,
              render: (_, record) => {
                const sData = record.standardsData[std.standard_name] || { attended: 0 };
                return <span style={{ fontWeight: 700, color: 'var(--primary-color, #1890ff)' }}>{sData.attended}</span>;
              }
            });

            columns.push({
              title: pctTitle,
              key: `${std.standard_name}_percentage`,
              align: 'center',
              width: 120,
              render: (_, record) => {
                const sData = record.standardsData[std.standard_name] || { percentage: '0.00%' };
                const numericPct = parseFloat(sData.percentage);
                let color = 'success';
                if (numericPct < 50) color = 'error';
                else if (numericPct < 75) color = 'warning';

                return (
                  <Tag color={color} style={{ fontWeight: 800, padding: '2px 8px', borderRadius: 6 }}>
                    {sData.percentage}
                  </Tag>
                );
              }
            });
          });

          return (
            <div key={gIdx} style={{ marginBottom: gIdx < reportData.length - 1 ? 32 : 0 }}>
              <div style={{
                display: 'flex',
                justify: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
                padding: '8px 12px',
                background: 'rgba(24, 144, 255, 0.06)',
                borderRadius: 8,
                border: '1px solid rgba(24, 144, 255, 0.15)'
              }}>
                <Text strong style={{ fontSize: 15, color: 'var(--primary-color, #1890ff)' }}>
                  📍 {cGroup.cluster_name} — {monthLabel}
                </Text>
                <Space>
                  {cGroup.standards.map((s, idx) => (
                    <Tag key={idx} color="blue" style={{ fontSize: 12, padding: '2px 10px', borderRadius: 10 }}>
                      {s.standard_name}: <strong>{s.total_class_done} Classes Conducted</strong>
                    </Tag>
                  ))}
                </Space>
              </div>

              <Table
                dataSource={cGroup.schools}
                columns={columns}
                rowKey={(r) => `${r.code}-${r.name}`}
                pagination={false}
                bordered
                size="small"
                scroll={{ x: 'max-content' }}
                summary={() => (
                  <Table.Summary.Row style={{ background: 'rgba(0,0,0,0.03)', fontWeight: 'bold' }}>
                    <Table.Summary.Cell index={0} colSpan={2}>
                      <span style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {monthLabel}
                      </span>
                    </Table.Summary.Cell>

                    {cGroup.standards.map((std, sIdx) => (
                      <React.Fragment key={sIdx}>
                        <Table.Summary.Cell index={2 + sIdx * 2} align="center">
                          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>TOTAL CLASS DONE</span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3 + sIdx * 2} align="center">
                          <Tag color="cyan" style={{ fontSize: 13, fontWeight: 800, padding: '2px 10px', margin: 0 }}>
                            {std.total_class_done}
                          </Tag>
                        </Table.Summary.Cell>
                      </React.Fragment>
                    ))}
                  </Table.Summary.Row>
                )}
              />
            </div>
          );
        })
      )}
    </Card>
  );
};

export default ClusterLiveAttendanceReportCard;
